import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Trash2, ChevronRight, Users, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = {
  gaji_bulanan: 'Gaji Bulanan',
  gaji_mingguan: 'Gaji Mingguan',
  kasbon: 'Kasbon',
  pelunasan_kasbon: 'Pelunasan Kasbon',
};
const TYPE_BADGE = {
  gaji_bulanan: 'bg-green-100 text-green-700',
  gaji_mingguan: 'bg-blue-100 text-blue-700',
  kasbon: 'bg-orange-100 text-orange-700',
  pelunasan_kasbon: 'bg-purple-100 text-purple-700',
};

const fmt = (n) => n ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n) : 'Rp 0';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

export default function StaffTransactions({ userRole }) {
  const [data, setData] = useState({ transactions: [], staff: [], kasbon_balances: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStaff, setFilterStaff] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    staff_email: '', transaction_type: 'gaji_bulanan',
    amount: '', transaction_date: new Date().toISOString().slice(0,10),
    notes: '', property_name: 'all',
  });
  const [properties, setProperties] = useState([]);

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchData = () => axios.get(`${BACKEND_URL}/api/admin/staff-transactions`, cfg())
    .then(r => setData(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    fetchData();
    axios.get(`${BACKEND_URL}/api/staff/properties`, cfg()).then(r => setProperties(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const staff = data.staff.find(s => s.email === form.staff_email);
      const payload = { ...form, staff_name: staff?.name || form.staff_email, property_name: form.property_name === 'all' ? '' : form.property_name };
      await axios.post(`${BACKEND_URL}/api/admin/staff-transactions`, payload, cfg());
      setSuccess('Transaksi berhasil dicatat');
      setShowForm(false);
      setForm({ staff_email: '', transaction_type: 'gaji_bulanan', amount: '', transaction_date: new Date().toISOString().slice(0,10), notes: '', property_name: 'all' });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus transaksi ini?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/staff-transactions/${id}`, cfg());
      setSelected(null); fetchData();
    } catch (e) { setError('Gagal menghapus'); }
  };

  const filtered = data.transactions.filter(t => {
    if (filterType !== 'all' && t.transaction_type !== filterType) return false;
    if (filterStaff !== 'all' && t.staff_email !== filterStaff) return false;
    return true;
  });

  // Summary per staff
  const staffWithBalance = data.staff.map(s => ({
    ...s, kasbon_balance: parseFloat(data.kasbon_balances[s.email] || 0)
  })).filter(s => s.kasbon_balance !== 0 || data.transactions.some(t => t.staff_email === s.email));

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
          <Users className="w-5 h-5 text-isk-teal" /> Transaksi Staff
        </h2>
        <Button onClick={() => setShowForm(true)} size="sm" className="bg-isk-teal text-white rounded-full">
          <Plus className="w-4 h-4 mr-1" /> Catat Transaksi
        </Button>
      </div>

      {/* Kasbon summary */}
      {staffWithBalance.filter(s => s.kasbon_balance > 0).length > 0 && (
        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200">
          <p className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Saldo Kasbon Belum Lunas</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {staffWithBalance.filter(s => s.kasbon_balance > 0).map(s => (
              <div key={s.email} className="p-2 rounded-lg bg-white/70 text-xs">
                <p className="font-medium text-isk-text">{s.name}</p>
                <p className="text-orange-600 font-semibold">{fmt(s.kasbon_balance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex gap-1 bg-white/40 rounded-xl p-1">
          {[['all','Semua'],['gaji_bulanan','Gaji Bulanan'],['gaji_mingguan','Gaji Mingguan'],['kasbon','Kasbon'],['pelunasan_kasbon','Pelunasan']].map(([k,l]) => (
            <button key={k} onClick={() => setFilterType(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterType===k ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              {l}
            </button>
          ))}
        </div>
        <Select value={filterStaff} onValueChange={setFilterStaff}>
          <SelectTrigger className="w-40 rounded-full text-xs h-8"><SelectValue placeholder="Semua Staff" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Staff</SelectItem>
            {data.staff.map(s => <SelectItem key={s.email} value={s.email}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
        <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_auto] gap-3 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
          <span>Staff</span><span>Jenis</span><span>Tanggal</span><span>Nominal</span><span></span>
        </div>
        {filtered.length === 0 && <div className="py-12 text-center text-isk-muted">Belum ada transaksi</div>}
        <div className="divide-y divide-white/20">
          {filtered.map((t, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_1.5fr_1.5fr_auto] gap-2 sm:gap-3 px-4 py-3 items-center hover:bg-white/30 transition-colors">
              <div>
                <p className="font-semibold text-isk-text text-sm">{t.staff_name}</p>
                {t.notes && <p className="text-xs text-isk-muted truncate">{t.notes}</p>}
              </div>
              <div className="hidden sm:block">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[t.transaction_type]}`}>{TYPE_LABEL[t.transaction_type]}</span>
              </div>
              <p className="hidden sm:block text-xs text-isk-muted">{fmtDate(t.transaction_date)}</p>
              <p className={`hidden sm:block text-sm font-semibold ${t.transaction_type === 'kasbon' ? 'text-orange-600' : t.transaction_type === 'pelunasan_kasbon' ? 'text-purple-600' : 'text-isk-teal'}`}>
                {t.transaction_type === 'kasbon' ? '-' : ''}{fmt(t.amount)}
              </p>
              <Button size="sm" onClick={() => setSelected(t)}
                className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                Detail <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Detail Popup */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">{selected.staff_name}</h3>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[['Jenis', TYPE_LABEL[selected.transaction_type]], ['Tanggal', fmtDate(selected.transaction_date)], ['Nominal', fmt(selected.amount)], ['Properti', selected.property_name||'-']].map(([l,v]) => (
                  <div key={l} className="p-2 rounded-lg bg-gray-50"><p className="text-isk-muted">{l}</p><p className="font-semibold text-isk-text">{v}</p></div>
                ))}
              </div>
              {selected.notes && <div className="p-3 rounded-xl bg-gray-50 text-xs"><p className="text-isk-muted mb-1">Catatan</p><p>{selected.notes}</p></div>}
              {selected.transaction_type === 'kasbon' && (
                <div className="p-3 rounded-xl bg-orange-50 border border-orange-200 text-xs">
                  <p className="text-orange-700 font-medium">Saldo kasbon {selected.staff_name}: {fmt(data.kasbon_balances[selected.staff_email]||0)}</p>
                </div>
              )}
            </div>
            {userRole === 'superadmin' && (
              <Button onClick={() => handleDelete(selected.id)} variant="outline"
                className="w-full mt-4 border-red-200 text-red-500 hover:bg-red-50 rounded-full text-sm">
                <Trash2 className="w-4 h-4 mr-1" /> Hapus
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-isk-text">Catat Transaksi Staff</h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label className="text-xs">Jenis Transaksi *</Label>
                <Select value={form.transaction_type} onValueChange={v => setForm(f => ({...f, transaction_type: v}))}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gaji_bulanan">Gaji Bulanan</SelectItem>
                    <SelectItem value="gaji_mingguan">Gaji Mingguan</SelectItem>
                    <SelectItem value="kasbon">Kasbon (Ambil)</SelectItem>
                    <SelectItem value="pelunasan_kasbon">Pelunasan Kasbon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Lokasi / Properti</Label>
                <Select value={form.property_name || 'all'} onValueChange={v => setForm(f => ({...f, property_name: v, staff_email: ''}))}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih lokasi (opsional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Lokasi</SelectItem>
                    {(properties || []).map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Staff *</Label>
                <Select value={form.staff_email} onValueChange={v => setForm(f => ({...f, staff_email: v}))}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih staff" /></SelectTrigger>
                  <SelectContent>
                    {(data.staff || [])
                      .filter(s => !form.property_name || form.property_name === 'all' || !s.property_name || s.property_name === form.property_name)
                      .map(s => (
                        <SelectItem key={s.email} value={s.email}>
                          {s.name}{s.property_name ? ` (${s.property_name})` : ''}
                          {(form.transaction_type === 'kasbon' || form.transaction_type === 'pelunasan_kasbon') && data.kasbon_balances[s.email] > 0 &&
                            ` · kasbon: ${fmt(data.kasbon_balances[s.email])}`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nominal (Rp) *</Label>
                  <Input type="number" min="0" value={form.amount} onChange={e => setForm(f=>({...f,amount:e.target.value}))} placeholder="0" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Tanggal *</Label>
                  <Input type="date" value={form.transaction_date} onChange={e => setForm(f=>({...f,transaction_date:e.target.value}))} className="mt-1 rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Catatan</Label>
                <Input value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder={form.transaction_type === 'gaji_bulanan' ? 'Gaji Juli 2026' : form.transaction_type === 'kasbon' ? 'Keperluan ...' : 'Opsional'} className="mt-1 rounded-xl" />
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-isk-teal text-white rounded-full">
                  {submitting ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
